import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../../images/logo.png';
import '../css/UserDashboard.css';
import '../css/UserDashboardPrototype.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import useIdleLogout from "../../hooks/useIdleLogout";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUser,
  // faTicket, // DISABLED: Tickets feature not yet implemented
  // faMessage, // DISABLED: Messages feature not yet implemented
  faFileLines,
  // faGear, // DISABLED: Change Tracking feature not yet implemented
  faArrowAltCircleRight,
  // faSnowboarding, // Travel is under Personal Registration tabs
  faUserFriends,
  // faCalendar, // DISABLED: Events feature not yet implemented
  // faFolder, // DISABLED: Forms feature not yet implemented
  // faCircleCheck, // DISABLED: Confirmation feature not yet implemented
  faSignOutAlt,
  faAnglesLeft,
  faBars,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import GlobalCompetitionBanner from '../ems/GlobalCompetitionBanner';

import {
  loadSavedProgress
} from '../../services/PanelDataService';

const SIDEBAR_COLLAPSED_KEY = 'userDashboardSidebarCollapsed';
const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : '/api');

// Athlete group ID
const ATHLETE_GROUP_ID = "6884aa884fb3247b61564ae5";

const UserDashboard = ({ handleLogout }) => {

  const [username, setUsername] = useState("");
  const [participants, setParticipants] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [loadingParticipant, setLoadingParticipant] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch (_) {}
      return next;
    });
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  const handleNavClick = (path) => {
    // No guestId query → Personal Registration always shows the logged-in user.
    navigate(path);
    closeMobileNav();
  };

  // Close mobile drawer when viewport grows past tablet
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) setMobileNavOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const location = useLocation();
  const navigate = useNavigate();
  useIdleLogout();

  // Load stored participant first name
  useEffect(() => {
    const storedName = localStorage.getItem("username");
    if (storedName && storedName !== "null" && storedName !== "undefined") {
      setUsername(storedName);
    }
  }, []);

  // Load participants + match participant by FIRST NAME
  useEffect(() => {
    if (!username) return;

    const loadParticipants = async () => {
      setLoadingParticipant(true);

      try {
        const res = await axios.get(`${API_URL}/ems/participants`);
        const list = Array.isArray(res.data) ? res.data : [];

        setParticipants(list);

        // Match participant by first name
        const matched = list.find(
          p =>
            p.person_id?.name?.toLowerCase() === username.toLowerCase()
      );

        if (matched) {
          const pid = matched._id;

          setGroupId(matched.group_id?._id || null);
          console.log("Matched participant:", matched);

          // 🚀 Load saved progress from backend
          loadSavedProgress(pid);
        } else {
          console.warn("No participant matched for:", username);
        }

      } catch (err) {
        console.error("Failed to load participants:", err);
      }

      setLoadingParticipant(false);
    };

    loadParticipants();
  }, [username]);

  // Determine if user is athlete
  const isAthlete = groupId === ATHLETE_GROUP_ID;

  console.log("UserDashboard loaded", { username, groupId, isAthlete });

  const FORM_IDS = {
    REGISTRATION: '68c7eefbd44999eedb541043',
    ACCREDITATION: '68e6505866f25479291b933f',
    TRAVEL: '68e781408e1384b2c283c3af',
    ACCOMMODATION: '68e784018e1384b2c283c3bb',
    GUEST: '68e785cb8e1384b2c283c3c5',
  };

  /** Personal Registration covers registration + accreditation + travel + accommodation + guest form tab. */
  const isNavItemActive = (item) => {
    if (Array.isArray(item.matchPrefixes) && item.matchPrefixes.length) {
      return item.matchPrefixes.some((prefix) => location.pathname.startsWith(prefix));
    }
    return location.pathname.startsWith(item.path);
  };

  const navItems = [
    { key: 'dashboard', path: '/user/dashboard', label: 'Dashboard', icon: faHome },
    { key: 'intro', path: '/user/introduction', label: 'Introduction', icon: faArrowAltCircleRight },

    {
      key: 'registration',
      path: `/user/form/view/registration/${FORM_IDS.REGISTRATION}`,
      label: 'Personal Registration',
      icon: faUser,
      matchPrefixes: [
        '/user/form/view/registration',
        '/user/form/view/accreditation',
        '/user/form/view/guest',
        ...(isAthlete
          ? []
          : ['/user/form/view/travel', '/user/form/view/accommodation']),
      ],
    },

    // DISABLED: { key: 'tickets', path: '#', label: 'Tickets & Hospitality', icon: faTicket },
    // Travel is a Personal Registration tab (not a separate sidebar item)

    {
      key: 'guest',
      path: '/user/guest-registration',
      label: 'Guest Registration',
      icon: faUserFriends,
      matchPrefixes: [
        '/user/guest-registration',
      ],
    },

    // DISABLED: { key: 'events', path: '#', label: 'Events', icon: faCalendar },
    // DISABLED: { key: 'messages', path: '#', label: 'Messages', icon: faMessage },
    { key: 'documents', path: '/user/esign-documents', label: 'Documents', icon: faFileLines },
    // DISABLED: { key: 'confirmation', path: '#', label: 'Confirmation', icon: faCircleCheck },
    // DISABLED: { key: 'forms', path: '#', label: 'Forms', icon: faFolder },
    // DISABLED: { key: 'settings', path: '#', label: 'Change Tracking', icon: faGear },
  ].filter(Boolean);

  return (
    <div
      className={[
        'd-flex user-dashboard-shell',
        sidebarCollapsed ? 'user-dashboard-shell--collapsed' : '',
        mobileNavOpen ? 'user-dashboard-shell--nav-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="user-sidebar-backdrop"
        aria-label="Close menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={closeMobileNav}
      />

      <div
        className={`user-sidebar-container d-flex flex-column ${sidebarCollapsed ? 'collapsed' : ''}`}
        id="user-sidebar"
      >
        <div className="user-logo-container d-flex align-items-center justify-content-center">
          <img src={logo} alt="User Dashboard Logo" className="user-logo" />
          <span className="user-logo-monogram" aria-hidden="true">IGF</span>
          <button
            type="button"
            className="user-sidebar-close"
            aria-label="Close menu"
            onClick={closeMobileNav}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="sidebar flex-grow-1">
          <ul className="nav flex-column px-0">
            {navItems.map((item) => (
              <li key={item.key} className="nav-item">
                <div
                  onClick={() => handleNavClick(item.path)}
                  className={`nav-link ${isNavItemActive(item) ? 'active' : ''}`}
                  title={item.label}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNavClick(item.path);
                    }
                  }}
                >
                  <span className="nav-icon-circle">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <span className="sidebar-label">{item.label}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="sidebar-footer">
            <button
              type="button"
              className="sidebar-collapse-row"
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <span className="sidebar-collapse-icon">
                <FontAwesomeIcon icon={faAnglesLeft} />
              </span>
              <span className="sidebar-footer-label">
                {sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="content d-flex flex-column flex-grow-1">
        <nav className="navbar navbar-light user-topbar">
          <button
            type="button"
            className="user-topbar__menu-btn"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
            aria-controls="user-sidebar"
            onClick={() => setMobileNavOpen(true)}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <div className="user-topbar__banner">
            <GlobalCompetitionBanner />
          </div>

          <h4 className="navbar-brand mb-0 user-topbar__welcome">
            <div className="dropdown">
              <button
                className="btn btn-light dropdown-toggle"
                id="userDropdown"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Welcome, {username || 'Guest'}
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                <li>
                  <div className="dropdown-item" onClick={() => navigate('/user/profile')}>Profile</div>
                </li>
                <li>
                  <div className="dropdown-item" onClick={() => navigate('/user/settings')}>Settings</div>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </h4>
        </nav>

        <div className="d-flex flex-grow-1 user-dashboard-body">
          <div className="content-full-width">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
